import { Component, effect, inject, input, output, signal } from '@angular/core';
import { NonNullableFormBuilder } from '@angular/forms';
import { NbToastrService } from '@nebular/theme';
import { InspectionDetailDto, InspectionService } from '@artemis-software/wr-api';
import { firstValueFrom } from 'rxjs';

@Component({
  standalone: false,
  selector: 'wr-inspection-notes',
  templateUrl: './inspection-notes.component.html',
  styleUrls: ['./inspection-notes.component.scss'],
})
export class InspectionNotesComponent {

  private readonly inspectionService = inject(InspectionService);
  private readonly toastrService = inject(NbToastrService);
  private readonly formBuilder = inject(NonNullableFormBuilder);

  readonly inspection = input.required<InspectionDetailDto>();
  readonly inspectionChange = output<InspectionDetailDto>();

  readonly noteControl = this.formBuilder.control('');
  readonly saving = signal(false);

  private lastInspectionId?: string;

  constructor() {
    effect(() => {
      const inspection = this.inspection();
      // Referenzwechsel derselben Begehung (z. B. Haupt-Speichern auf „Allgemein")
      // dürfen eine noch nicht gespeicherte Notiz nicht überschreiben — nur bei
      // pristine Control oder echtem Begehungswechsel zurücksetzen.
      if (this.noteControl.dirty && inspection.id === this.lastInspectionId) {
        return;
      }
      this.lastInspectionId = inspection.id;
      this.noteControl.setValue(inspection.note ?? '', { emitEvent: false });
      this.noteControl.markAsPristine();
    });
  }

  async save(): Promise<void> {
    if (this.saving()) {
      return;
    }
    this.saving.set(true);
    try {
      const note = this.noteControl.value;
      const updated = await firstValueFrom(this.inspectionService.updateNote(this.inspection().id, { note }));
      // Während des Requests weitergetippter Text ist nicht gesendet worden und
      // muss dirty bleiben, sonst verschluckt ihn der nächste Effect-Lauf.
      if (this.noteControl.value === note) {
        this.noteControl.markAsPristine();
      }
      this.inspectionChange.emit(updated);
      this.toastrService.success('Notizen gespeichert', 'Erfolg');
    } catch (error) {
      // Kein Re-throw: das Control bleibt dirty, der getippte Text erhalten.
      console.error('Notizen konnten nicht gespeichert werden', error);
      this.toastrService.danger('Notizen konnten nicht gespeichert werden.', 'Fehler');
    } finally {
      this.saving.set(false);
    }
  }
}
